import React from "react";
import { motion } from "framer-motion";
import { CreditCard } from "lucide-react";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
};

export interface AttractCTAProps {
  onScanClick?: () => void;
  signupQRValue?: string;
}

const AttractCTA: React.FC<AttractCTAProps> = ({
  onScanClick,
  signupQRValue = "https://nespresso.com/signup",
}) => {
  return (
    <motion.div
      className="w-full px-8 py-6"
      style={{ backgroundColor: NESPRESSO.black }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-8">
        {/* Primary CTA - Scan Loyalty Card */}
        <motion.button
          onClick={onScanClick}
          className="flex-1 flex items-center justify-center gap-4 py-5 px-8 text-white font-medium transition-all"
          style={{
            backgroundColor: NESPRESSO.brown,
            borderRadius: "3px",
          }}
          whileHover={{
            backgroundColor: NESPRESSO.brownHover,
            scale: 1.02,
          }}
          whileTap={{ scale: 0.98 }}
        >
          <CreditCard size={24} strokeWidth={1.5} />
          <span className="text-lg">Scan Loyalty Card to start earning</span>
        </motion.button>

        {/* Divider */}
        <div className="flex flex-col items-center">
          <div className="h-8 w-px bg-white/20" />
          <span className="text-white/40 text-sm py-2">or</span>
          <div className="h-8 w-px bg-white/20" />
        </div>

        {/* Secondary - Sign Up QR */}
        <motion.div
          className="flex items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div
            className="w-20 h-20 rounded-lg flex items-center justify-center"
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "8px",
            }}
          >
            {/* QR Code placeholder - in production would use a real QR library */}
            <QRCodePlaceholder value={signupQRValue} />
          </div>
          <div className="flex flex-col">
            <span
              className="text-sm font-medium uppercase tracking-wider"
              style={{ color: NESPRESSO.gold }}
            >
              New here?
            </span>
            <span className="text-white text-base">Scan to Sign Up</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Simple QR code placeholder - generates a deterministic pattern from the value
const QRCodePlaceholder: React.FC<{ value: string }> = ({ value }) => {
  // Generate a simple visual pattern based on the string hash
  const hash = value.split("").reduce((acc, char) => {
    return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
  }, 0);

  const cells = [];
  const size = 7;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Create a pattern that looks like a QR code
      const isCorner =
        (x < 2 && y < 2) ||
        (x > size - 3 && y < 2) ||
        (x < 2 && y > size - 3);
      const isCornerInner =
        (x === 0 && y === 0) ||
        (x === size - 1 && y === 0) ||
        (x === 0 && y === size - 1);
      const isData = ((hash >> (x + y * size)) & 1) === 1;

      const filled = isCorner || isCornerInner || isData;

      cells.push(
        <div
          key={`${x}-${y}`}
          className={`${filled ? "bg-black" : "bg-transparent"}`}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      );
    }
  }

  return (
    <div
      className="grid gap-0.5 w-full h-full"
      style={{
        gridTemplateColumns: `repeat(${size}, 1fr)`,
        gridTemplateRows: `repeat(${size}, 1fr)`,
      }}
    >
      {cells}
    </div>
  );
};

export default AttractCTA;
